/**
 * Image Utilities
 *
 * Helper functions for image processing:
 * - Image compression (WebP preferred, JPEG fallback)
 * - File size validation
 * - Type validation
 * - Preview generation
 */

/**
 * Maximum file size in bytes (25MB)
 * Modern phone cameras (iPhone 15/16) produce 12-48MP photos that can be 10-25MB.
 * Images are compressed to ~100-500KB before upload, so this limit is only for
 * the initial raw file selection — not the final upload size.
 */
export const MAX_FILE_SIZE = 25 * 1024 * 1024

/**
 * Maximum image dimension for compression.
 * 800px is plenty for Gemini Vision API (recommended ≥ 512px)
 * and mobile card display (max ~192px on screen).
 * Keeps final file sizes to 30-150KB typically.
 */
export const COMPRESS_MAX_WIDTH = 800

/**
 * Supported image MIME types
 */
export const SUPPORTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif'
]

/**
 * HEIC/HEIF formats that need conversion
 */
export const HEIC_FORMATS = ['image/heic', 'image/heif']

/**
 * Cached result of WebP encoding support check.
 * Tested once on first use to avoid double-encoding on every image.
 */
let webpEncodingSupported: boolean | null = null

/**
 * Detects whether the browser supports WebP encoding via canvas.toBlob.
 * Tests once and caches the result for all subsequent calls.
 */
function checkWebPSupport(): Promise<boolean> {
  if (webpEncodingSupported !== null) {
    return Promise.resolve(webpEncodingSupported)
  }

  return new Promise((resolve) => {
    const canvas = document.createElement('canvas')
    canvas.width = 1
    canvas.height = 1
    canvas.toBlob(
      (blob) => {
        webpEncodingSupported = blob?.type === 'image/webp'
        resolve(webpEncodingSupported)
      },
      'image/webp',
      0.8
    )
  })
}

/**
 * Checks if a file is HEIC/HEIF format that requires conversion
 */
export function isHeicFormat(file: File): boolean {
  return HEIC_FORMATS.includes(file.type) || file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')
}

/**
 * Validates if a file is an image
 */
export function isValidImageFile(file: File): boolean {
  return SUPPORTED_IMAGE_TYPES.includes(file.type)
}

/**
 * Validates if a file is within the size limit
 */
export function isValidFileSize(file: File): boolean {
  return file.size <= MAX_FILE_SIZE
}

/**
 * Compresses an image, preferring WebP but falling back to JPEG
 * if the browser doesn't support WebP encoding (e.g. older iOS Safari).
 *
 * Feature-detects WebP support once on first call, then uses the
 * correct format directly — avoiding double-encoding overhead.
 *
 * @param file - The image file to compress
 * @param quality - Compression quality (0-1, default 0.8)
 * @param maxWidth - Maximum width for resizing (default 1280)
 * @returns Promise<Blob> - Compressed image blob (WebP or JPEG)
 */
export async function compressImage(
  file: File,
  quality: number = 0.65,
  maxWidth: number = COMPRESS_MAX_WIDTH
): Promise<Blob> {
  const useWebP = await checkWebPSupport()
  const mimeType = useWebP ? 'image/webp' : 'image/jpeg'

  return new Promise((resolve, reject) => {
    const img = new Image()
    const reader = new FileReader()

    reader.onload = (e) => {
      img.src = e.target?.result as string
    }

    img.onload = () => {
      let width = img.width
      let height = img.height

      if (width > maxWidth) {
        height = (height * maxWidth) / width
        width = maxWidth
      }

      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Failed to get canvas context'))
        return
      }

      ctx.drawImage(img, 0, 0, width, height)

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Failed to compress image'))
          }
        },
        mimeType,
        quality
      )
    }

    img.onerror = () => {
      reject(new Error('Failed to load image'))
    }

    reader.onerror = () => {
      reject(new Error('Failed to read file'))
    }

    reader.readAsDataURL(file)
  })
}

/** @deprecated Use compressImage instead */
export const compressToWebP = compressImage

/**
 * Generates a preview URL for an image file
 *
 * @param file - The image file
 * @returns Promise<string> - Data URL for preview
 */
export async function generatePreview(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      resolve(e.target?.result as string)
    }

    reader.onerror = () => {
      reject(new Error('Failed to generate preview'))
    }

    reader.readAsDataURL(file)
  })
}

/**
 * Converts HEIC/HEIF image to a browser-compatible format (JPEG)
 *
 * @param file - The HEIC/HEIF file to convert
 * @returns Promise<Blob> - Converted image as JPEG blob
 * @throws Error if conversion fails
 */
export async function convertHeicToBlob(file: File): Promise<Blob> {
  try {
    // Dynamic import to avoid loading heic2any unless needed
    const heic2any = (await import('heic2any')).default

    // Convert HEIC to JPEG — much smaller than PNG for photos
    // (PNG at quality:1 from 12MP iPhone can produce 15-20MB files)
    const blobs = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.85
    }) as Blob | Blob[]

    // heic2any returns a single Blob or an array of Blobs
    return Array.isArray(blobs) ? blobs[0] : blobs
  } catch (error) {
    throw new Error(
      `Failed to convert HEIC image: ${error instanceof Error ? error.message : 'Unknown error'}`
    )
  }
}

/**
 * Formats file size for display
 *
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

/**
 * Get the URL for a card image served via the API proxy
 *
 * @param imageKey - R2 key of the image (e.g., "images/abc123.jpg")
 * @returns Full URL to the image, or null if no key
 */
export function getImageUrl(imageKey: string | null | undefined): string | null {
  if (!imageKey) return null
  // Use the API_URL from environment to construct full URL
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787'
  return `${apiUrl}/api/images/${imageKey}`
}
